/**
 * FoodNewsFeed — geo-scoped food news widget.
 *
 * Drops onto any FoodPulse geographic page (city, state, country) and
 * renders a magazine-style feed of food news, recalls, and illness
 * outbreaks pulled from Google News + official CDC/FDA/USDA/WHO/EFSA
 * feeds. Cached server-side for 6 hours per geography.
 *
 * Usage:
 *   <FoodNewsFeed country="US" state="OR" city="Portland" title="Portland Food News" />
 */
import { useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, Zap, Newspaper, ExternalLink, Loader2 } from "lucide-react";
import { http } from "@/lib/api";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const AMBER  = "#E4A140";
const PURPLE = "#7C3AED";
const RED    = "#B91C1C";
const ORANGE = "#EA580C";

const CATS = [
  { key: "all",     label: "All",       icon: Newspaper,      color: GREEN },
  { key: "recall",  label: "Recalls",   icon: AlertTriangle,  color: RED },
  { key: "illness", label: "Outbreaks", icon: Zap,            color: ORANGE },
  { key: "news",    label: "News",      icon: Newspaper,      color: PURPLE },
];

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60)     return "just now";
    if (diff < 3600)   return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400)  return `${Math.round(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

function NewsCard({ item }) {
  const cat = CATS.find((c) => c.key === item.category) || CATS[3];
  const Icon = cat.icon;
  return (
    <a
      href={item.link}
      target="_blank"
      rel="noreferrer"
      className="block rounded-2xl border bg-white p-4 hover:shadow-md transition"
      style={{ borderColor: "#E9D8B4" }}
      data-testid={`food-news-item-${(item.title || "").slice(0, 20).replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-widest font-bold">
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5" style={{ background: `${cat.color}18`, color: cat.color }}>
          <Icon size={10} /> {cat.label}
        </span>
        <span className="text-gray-500 font-mono normal-case tracking-normal">{item.source}</span>
        <span className="text-gray-400 font-mono normal-case tracking-normal">· {timeAgo(item.published_at)}</span>
        <ExternalLink size={10} className="ml-auto opacity-40" />
      </div>
      <div className="mt-2 font-bold text-sm leading-tight" style={{ color: GREEN }}>{item.title}</div>
      {item.summary && (
        <div className="mt-1 text-xs text-gray-700 leading-relaxed line-clamp-3">{item.summary}</div>
      )}
    </a>
  );
}

export default function FoodNewsFeed({ country, state, county, city, title, className = "", limit = 24 }) {
  const [data, setData]     = useState(null);
  const [busy, setBusy]     = useState(true);
  const [cat, setCat]       = useState("all");
  const [err, setErr]       = useState(null);

  const load = async (force = false) => {
    setBusy(true); setErr(null);
    try {
      const r = await http.get("/food-news", {
        params: { country, state, county, city, limit, force_refresh: force || undefined },
        timeout: 25000,
      });
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "News fetch failed"); }
    setBusy(false);
  };
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [country, state, county, city]);

  const items = data?.items || [];
  const filtered = cat === "all" ? items : items.filter((i) => i.category === cat);
  const counts   = data?.categories || {};
  const place    = city || county || state || country || "your area";
  const heading  = title || `${place} · Food News, Recalls & Outbreaks`;

  return (
    <section className={`rounded-3xl border bg-white ${className}`} style={{ borderColor: "#E9D8B4" }} data-testid="food-news-feed">
      <header className="p-5 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: "#F5E6C4", background: "linear-gradient(135deg,#FEF3C7,#F0E2C7)" }}>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: PURPLE }}>Auto-curated · Google News + CDC/FDA/USDA/WHO</div>
          <h2 className="text-xl md:text-2xl font-black leading-tight" style={{ color: GREEN }} data-testid="food-news-title">{heading}</h2>
        </div>
        <button onClick={() => load(true)} className="rounded-full border px-3 py-2 text-sm font-semibold inline-flex items-center gap-1" style={{ borderColor: GREEN, color: GREEN }} data-testid="food-news-refresh">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </header>

      <nav className="px-4 pt-3 pb-2 border-b overflow-x-auto flex gap-2 whitespace-nowrap" style={{ borderColor: "#F5E6C4" }} data-testid="food-news-cats">
        {CATS.map((c) => {
          const n = c.key === "all" ? items.length : (counts[c.key] || 0);
          return (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              className={`text-xs font-semibold rounded-full px-3 py-1 border transition`}
              style={cat === c.key
                ? { background: c.color, color: "white", borderColor: c.color }
                : { borderColor: "#E9D8B4", color: c.color }}
              data-testid={`food-news-cat-${c.key}`}
            >
              {c.label} {n > 0 && <span className="opacity-70">({n})</span>}
            </button>
          );
        })}
      </nav>

      <div className="p-4">
        {busy ? (
          <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>
        ) : err ? (
          <div className="rounded-xl border p-3 text-sm" style={{ borderColor: "#F0C0C0", background: "#FEE7DA", color: "#7A3E00" }} data-testid="food-news-err">{err}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-600" data-testid="food-news-empty">
            No {cat === "all" ? "news" : cat + " items"} for {place} right now.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2" data-testid="food-news-list">
            {filtered.map((it, i) => <NewsCard key={i} item={it} />)}
          </div>
        )}
      </div>

      <footer className="px-4 py-3 border-t text-[11px] text-gray-500" style={{ borderColor: "#F5E6C4" }}>
        Sources cached for 6 hours · click any card to open on the publisher's site
      </footer>
    </section>
  );
}
